import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Row, Col, Form, Button, Alert, Table, Spinner, Badge, Modal } from 'react-bootstrap';
import PrivateLayout from '../../layouts/PrivateLayout';
import Panel from '../../common/Panel';
import dayjs from '../../../lib/day';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const SMC_S_REQUIRED_FIELDS = [
  'singleNoChildren',
  'marriedNoChildren',
  'singleWith1Child',
  'marriedWith1Child',
  'additionalChildUnder18',
  'additionalChildInSchool',
];

const normalizeSmcSRates = (smcSRates) => {
  if (!smcSRates || typeof smcSRates !== 'object') return null;

  const normalized = {};
  for (const field of SMC_S_REQUIRED_FIELDS) {
    const value = Number(smcSRates[field]);
    if (!Number.isFinite(value) || value < 0) return null;
    normalized[field] = value;
  }

  const spouseAidAndAttendance = Number(smcSRates.spouseAidAndAttendance);
  normalized.spouseAidAndAttendance = Number.isFinite(spouseAidAndAttendance) && spouseAidAndAttendance >= 0
    ? spouseAidAndAttendance
    : null;

  return normalized;
};

const calculateSmcSAmount = (smcSRates, isMarried, childrenUnder18, childrenInSchool, includeSpouseAid) => {
  const under18 = Math.max(0, Math.floor(Number(childrenUnder18) || 0));
  const inSchool = Math.max(0, Math.floor(Number(childrenInSchool) || 0));
  const totalChildren = under18 + inSchool;

  let total = totalChildren > 0
    ? (isMarried ? smcSRates.marriedWith1Child : smcSRates.singleWith1Child)
    : (isMarried ? smcSRates.marriedNoChildren : smcSRates.singleNoChildren);

  if (totalChildren > 1) {
    let additionalUnder18 = under18;
    let additionalSchool = inSchool;

    // One child is already included in the with-1-child base row.
    if (additionalUnder18 > 0) {
      additionalUnder18 -= 1;
    } else if (additionalSchool > 0) {
      additionalSchool -= 1;
    }

    total += smcSRates.additionalChildUnder18 * additionalUnder18;
    total += smcSRates.additionalChildInSchool * additionalSchool;
  }

  if (isMarried && includeSpouseAid && Number.isFinite(smcSRates.spouseAidAndAttendance)) {
    total += smcSRates.spouseAidAndAttendance;
  }

  return total;
};

const buildSmcSConfigurationRows = (smcSRates) => {
  const normalized = normalizeSmcSRates(smcSRates);
  if (!normalized) return null;

  const rows = [];
  const hasSpouseAidAndAttendance = Number.isFinite(normalized.spouseAidAndAttendance);

  for (const isMarried of [false, true]) {
    for (let childrenUnder18 = 0; childrenUnder18 <= 3; childrenUnder18++) {
      for (let childrenInSchool = 0; childrenInSchool <= 2; childrenInSchool++) {
        rows.push({
          isMarried,
          childrenUnder18,
          childrenInSchool,
          spouseAidAndAttendance: false,
          amount: calculateSmcSAmount(
            normalized,
            isMarried,
            childrenUnder18,
            childrenInSchool,
            false,
          ),
        });

        if (isMarried && hasSpouseAidAndAttendance) {
          rows.push({
            isMarried,
            childrenUnder18,
            childrenInSchool,
            spouseAidAndAttendance: true,
            amount: calculateSmcSAmount(
              normalized,
              isMarried,
              childrenUnder18,
              childrenInSchool,
              true,
            ),
          });
        }
      }
    }
  }

  return {
    rows,
    hasSpouseAidAndAttendance,
  };
};

const SmcSConfigurationsTable = ({ smcSRates }) => {
  const config = buildSmcSConfigurationRows(smcSRates);
  if (!config || !Array.isArray(config.rows) || config.rows.length === 0) {
    return (
      <div className="text-muted small">
        SMC-S rates are not configured for this schedule.
      </div>
    );
  }

  return (
    <>
      <Table size="sm" bordered responsive className="mb-2">
        <thead>
          <tr>
            <th className="text-center">Married</th>
            <th className="text-center">Kids &lt; 18</th>
            <th className="text-center">Kids 18-24 (School)</th>
            <th className="text-center">Spouse A&amp;A</th>
            <th className="text-end">SMC-S Monthly Amount</th>
          </tr>
        </thead>
        <tbody>
          {config.rows.map((row) => (
            <tr key={`${row.isMarried}-${row.childrenUnder18}-${row.childrenInSchool}-${row.spouseAidAndAttendance ? 'aa' : 'std'}`}>
              <td className="text-center">{row.isMarried ? 'Yes' : 'No'}</td>
              <td className="text-center">{row.childrenUnder18}</td>
              <td className="text-center">{row.childrenInSchool}</td>
              <td className="text-center">{row.isMarried ? (row.spouseAidAndAttendance ? 'Yes' : 'No') : '-'}</td>
              <td className="text-end">{formatCurrency(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="text-muted small">
        These are SMC-S profile amounts before any additive SMC-K awards.
        {!config.hasSpouseAidAndAttendance && ' Spouse Aid and Attendance is not configured in this schedule.'}
      </div>
    </>
  );
};

const RateTable = ({ rateData, title }) => {
  if (!rateData || !rateData.chart) return null;

  const ratings = ['10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'];

  return (
    <div className="mb-4">
      {title && <h5 className="mb-3">{title}</h5>}

      <h6>Base Rates (Veteran Alone)</h6>
      <Table size="sm" bordered responsive className="mb-3">
        <thead>
          <tr>
            <th>Rating</th>
            {ratings.map((r) => (
              <th key={r} className="text-center">
                {r}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Single</strong>
            </td>
            {rateData.chart.single.base.map((val, i) => (
              <td key={i} className="text-end">
                {formatCurrency(val)}
              </td>
            ))}
          </tr>
          <tr>
            <td>
              <strong>Married</strong>
            </td>
            {rateData.chart.married.base.map((val, i) => (
              <td key={i} className="text-end">
                {formatCurrency(val)}
              </td>
            ))}
          </tr>
        </tbody>
      </Table>

      <h6>With 1 Child</h6>
      <Table size="sm" bordered responsive className="mb-3">
        <thead>
          <tr>
            <th>Rating</th>
            {ratings.map((r) => (
              <th key={r} className="text-center">
                {r}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Single + 1 Child</strong>
            </td>
            {rateData.chart.single.dep1.map((val, i) => (
              <td key={i} className="text-end">
                {formatCurrency(val)}
              </td>
            ))}
          </tr>
          <tr>
            <td>
              <strong>Married + 1 Child</strong>
            </td>
            {rateData.chart.married.dep1.map((val, i) => (
              <td key={i} className="text-end">
                {formatCurrency(val)}
              </td>
            ))}
          </tr>
        </tbody>
      </Table>

      <h6>Additional Dependent Rates</h6>
      <Table size="sm" bordered responsive className="mb-3">
        <thead>
          <tr>
            <th>Type</th>
            {ratings.map((r) => (
              <th key={r} className="text-center">
                {r}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Each Child Under 18</strong>
            </td>
            {rateData.childRates.map((val, i) => (
              <td key={i} className="text-end">
                {formatCurrency(val)}
              </td>
            ))}
          </tr>
          <tr>
            <td>
              <strong>School Child (18-24)</strong>
            </td>
            {rateData.schoolChildRates.map((val, i) => (
              <td key={i} className="text-end">
                {formatCurrency(val)}
              </td>
            ))}
          </tr>
        </tbody>
      </Table>

      <div className="mb-3">
        <strong>SMC-K Rate:</strong> {formatCurrency(rateData.smcKRate)}
      </div>

      <div className="mb-3">
        <h6 className="mb-2"><strong>SMC-S Rates</strong></h6>
        <SmcSConfigurationsTable smcSRates={rateData.smcSRates} />
      </div>
    </div>
  );
};

const VaRatesManager = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Current active rates
  const [activeRates, setActiveRates] = useState(null);

  // All schedules
  const [schedules, setSchedules] = useState([]);

  // Parse URL flow
  const [parseUrl, setParseUrl] = useState('https://www.va.gov/disability/compensation-rates/veteran-rates/');
  const [parsing, setParsing] = useState(false);
  const [parsedRates, setParsedRates] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState('');

  // Activation modal
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activatingSchedule, setActivatingSchedule] = useState(null);
  const [activating, setActivating] = useState(false);
  const [activateImmediately, setActivateImmediately] = useState(true);

  // Preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Preview filters
  const [previewMarried, setPreviewMarried] = useState('all'); // 'all', 'true', 'false'
  const [previewChildrenUnder18, setPreviewChildrenUnder18] = useState('all'); // 'all', '0', '1', '2', '3'
  const [previewChildrenInSchool, setPreviewChildrenInSchool] = useState('all'); // 'all', '0', '1', '2'
  const [previewSmcK, setPreviewSmcK] = useState('all'); // 'all', '0', '1', '2'

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editRateData, setEditRateData] = useState(null);
  const [editEffectiveDate, setEditEffectiveDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [activeRes, schedulesRes] = await Promise.all([
        axios.get('/admin/va-rates/active').catch(() => ({ data: null })),
        axios.get('/admin/va-rates'),
      ]);

      if (activeRes.data && activeRes.data.rateData) {
        setActiveRates(activeRes.data.rateData);
      }

      if (schedulesRes.data && schedulesRes.data.schedules) {
        setSchedules(schedulesRes.data.schedules);
      }
    } catch (e) {
      setError('Failed to load rate data');
    } finally {
      setLoading(false);
    }
  };

  const handleParseUrl = async () => {
    if (!parseUrl) {
      setError('Please enter a URL');
      return;
    }

    setParsing(true);
    setError(null);
    setParsedRates(null);

    try {
      const res = await axios.post('/admin/va-rates/parse-url', { url: parseUrl });
      if (res.data && res.data.rateData) {
        setParsedRates(res.data.rateData);
        if (res.data.rateData.effectiveDate) {
          setEffectiveDate(res.data.rateData.effectiveDate);
        }
        setSuccess('Rates parsed successfully! Review below and save as draft.');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to parse rates from URL');
    } finally {
      setParsing(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!parsedRates) {
      setError('No parsed rates to save');
      return;
    }

    if (!effectiveDate) {
      setError('Please enter an effective date');
      return;
    }

    setError(null);
    try {
      await axios.post('/admin/va-rates', {
        effectiveDate,
        sourceUrl: parseUrl,
        rateData: parsedRates,
        notes: `Parsed from ${parseUrl}`,
      });

      setSuccess('Draft schedule saved! You can now activate it.');
      setParsedRates(null);
      setEffectiveDate('');
      loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save draft');
    }
  };

  const handleActivate = async () => {
    if (!activatingSchedule) return;

    setActivating(true);
    setError(null);

    try {
      const res = await axios.post(`/admin/va-rates/${activatingSchedule.scheduleId}/activate`, {
        activateImmediately,
      });
      setSuccess(res.data.message || 'Rate schedule activated!');
      setShowActivateModal(false);
      setActivatingSchedule(null);
      setActivateImmediately(true); // Reset for next time
      loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to activate schedule');
    } finally {
      setActivating(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;

    setError(null);
    try {
      await axios.delete(`/admin/va-rates/${scheduleId}`);
      setSuccess('Draft deleted');
      loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete draft');
    }
  };

  const handlePreview = async (schedule) => {
    setLoadingPreview(true);
    setShowPreviewModal(true);
    setPreviewData(null);

    try {
      const res = await axios.post(`/admin/va-rates/${schedule.scheduleId}/preview`);
      setPreviewData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load preview');
      setShowPreviewModal(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleEdit = async (schedule) => {
    try {
      const res = await axios.get(`/admin/va-rates/${schedule.scheduleId}`);
      const fetchedSchedule = res.data.schedule;
      const rateData = typeof fetchedSchedule.rateData === 'string'
        ? JSON.parse(fetchedSchedule.rateData)
        : fetchedSchedule.rateData;

      setEditingSchedule(fetchedSchedule);
      setEditRateData(JSON.parse(JSON.stringify(rateData))); // Deep clone
      setEditEffectiveDate(dayjs(fetchedSchedule.effectiveDate).format('YYYY-MM-DD'));
      setShowEditModal(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load schedule for editing');
    }
  };

  const handleEditRateChange = (path, index, value) => {
    const numValue = parseFloat(value) || 0;
    setEditRateData((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (path === 'smcKRate') {
        updated.smcKRate = numValue;
      } else {
        const parts = path.split('.');
        if (parts.length === 3) {
          // e.g., 'chart.single.base'
          updated[parts[0]][parts[1]][parts[2]][index] = numValue;
        } else if (parts.length === 1) {
          // e.g., 'childRates'
          updated[parts[0]][index] = numValue;
        }
      }
      return updated;
    });
  };

  const handleSaveEdit = async () => {
    if (!editingSchedule || !editRateData) return;

    setSaving(true);
    setError(null);

    try {
      await axios.put(`/admin/va-rates/${editingSchedule.scheduleId}`, {
        effectiveDate: editEffectiveDate,
        rateData: editRateData,
      });

      setSuccess('Draft schedule updated successfully!');
      setShowEditModal(false);
      setEditingSchedule(null);
      setEditRateData(null);
      loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (schedule) => {
    if (schedule.isCurrentlyEffective) {
      return <Badge bg="success">Active (In Effect)</Badge>;
    }
    if (schedule.isPending) {
      return <Badge bg="info">Scheduled</Badge>;
    }
    switch (schedule.status) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'draft':
        return <Badge bg="warning">Draft</Badge>;
      case 'archived':
        return <Badge bg="secondary">Archived</Badge>;
      default:
        return <Badge bg="info">{schedule.status}</Badge>;
    }
  };

  if (loading) {
    return (
      <PrivateLayout>
        <Row className="justify-content-center py-5">
          <Col xs="auto">
            <Spinner animation="border" />
          </Col>
        </Row>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <h2 className="mb-4">VA Disability Compensation Rates</h2>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {/* Current Active Rates */}
            <div className="mb-5">
              <h4>Current Active Rates</h4>
              {activeRates ? (
                <RateTable rateData={activeRates} />
              ) : (
                <Alert variant="warning">No active rate schedule configured.</Alert>
              )}
            </div>

            {/* Manual Retell Update Note */}
            <Alert variant="info" className="mb-4">
              <Alert.Heading className="h6 mb-2">
                <strong>📞 Manual Step: Update Retell AI Agent</strong>
              </Alert.Heading>
              <p className="mb-2">
                After activating new rates, you must manually update the Retell AI agent with the new
                payment amounts:
              </p>
              <ol className="mb-2 ps-3">
                <li>
                  Run: <code>node scripts/retell_va_call_list_amounts.js</code>
                </li>
                <li>Copy the list of amounts from the output</li>
                <li>
                  In Retell, navigate to the <strong>Extract Variables Step</strong> in the VA call
                  flow
                </li>
                <li>
                  Replace the existing amounts in the &quot;amount&quot; prompt with the new list
                </li>
              </ol>
              <small className="text-muted">
                This tells the AI which payment amounts are valid for the current rate schedule.
              </small>
            </Alert>

            <hr />

            {/* Parse New Rates */}
            <div className="mb-5">
              <h4>Update Rates from VA Website</h4>
              <p className="text-muted">
                Enter the VA compensation rates URL. The system will use AI to extract all rate
                values automatically.
              </p>

              <Row className="mb-3">
                <Col md={8}>
                  <Form.Control
                    type="url"
                    placeholder="https://www.va.gov/disability/compensation-rates/veteran-rates/"
                    value={parseUrl}
                    onChange={(e) => setParseUrl(e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Button
                    variant="primary"
                    onClick={handleParseUrl}
                    disabled={parsing || !parseUrl}
                  >
                    {parsing ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Parsing...
                      </>
                    ) : (
                      'Parse Rates'
                    )}
                  </Button>
                </Col>
              </Row>

              {parsedRates && (
                <div className="border rounded p-3 bg-light">
                  <h5 className="text-success mb-3">Parsed Rates - Review Below</h5>

                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Label>Effective Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <RateTable rateData={parsedRates} />

                  <div className="d-flex gap-2">
                    <Button variant="success" onClick={handleSaveAsDraft}>
                      Save as Draft
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setParsedRates(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <hr />

            {/* All Schedules */}
            <div>
              <h4>All Rate Schedules</h4>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Effective Date</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Approved</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        No rate schedules found
                      </td>
                    </tr>
                  ) : (
                    schedules.map((schedule) => (
                      <tr key={schedule.scheduleId}>
                        <td>
                          {dayjs(schedule.effectiveDate).format('MMM D, YYYY')}
                          {schedule.isPending && (
                            <div className="text-muted small">
                              Becomes effective at midnight ET
                            </div>
                          )}
                        </td>
                        <td>{getStatusBadge(schedule)}</td>
                        <td>{dayjs(schedule.createdAt).format('MMM D, YYYY h:mm A')}</td>
                        <td>
                          {schedule.approvedAt
                            ? dayjs(schedule.approvedAt).format('MMM D, YYYY h:mm A')
                            : '-'}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handlePreview(schedule)}
                            >
                              Preview
                            </Button>
                            {schedule.status === 'draft' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handleEdit(schedule)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => {
                                    setActivatingSchedule(schedule);
                                    setShowActivateModal(true);
                                  }}
                                >
                                  Activate
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(schedule.scheduleId)}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Panel>
        </Col>
      </Row>

      {/* Activate Confirmation Modal */}
      <Modal show={showActivateModal} onHide={() => setShowActivateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Activate Rate Schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activatingSchedule && (
            <>
              <p className="mb-3">
                <strong>Schedule Effective Date:</strong>{' '}
                {dayjs(activatingSchedule.effectiveDate).format('MMM D, YYYY')}
              </p>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">When should these rates take effect?</Form.Label>
                <Form.Check
                  type="radio"
                  id="activate-immediately"
                  name="activateOption"
                  label="Activate immediately (today)"
                  checked={activateImmediately}
                  onChange={() => setActivateImmediately(true)}
                  className="mb-2"
                />
                <Form.Check
                  type="radio"
                  id="activate-scheduled"
                  name="activateOption"
                  label={`Schedule for ${dayjs(activatingSchedule.effectiveDate).format('MMM D, YYYY')} (midnight Eastern Time)`}
                  checked={!activateImmediately}
                  onChange={() => setActivateImmediately(false)}
                />
              </Form.Group>

              {activateImmediately ? (
                <Alert variant="info" className="mb-0">
                  <strong>Immediate Activation:</strong> The current active schedule will be
                  archived and these rates will take effect right away.
                </Alert>
              ) : (
                <Alert variant="info" className="mb-0">
                  <strong>Scheduled Activation:</strong> These rates will automatically become
                  effective at midnight Eastern Time on{' '}
                  {dayjs(activatingSchedule.effectiveDate).format('MMM D, YYYY')}. The current
                  rates will remain in effect until then.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActivateModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleActivate} disabled={activating}>
            {activating ? (
              <>
                <Spinner size="sm" className="me-2" />
                {activateImmediately ? 'Activating...' : 'Scheduling...'}
              </>
            ) : activateImmediately ? (
              'Activate Now'
            ) : (
              'Schedule Activation'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Rate Schedule Preview - Monthly Compensation Matrix</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingPreview ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : previewData ? (
            <>
              {/* Raw Rate Table Summary */}
              {previewData.rateTable && (
                <div className="mb-4 p-3 bg-light rounded">
                  <h6 className="mb-2">SMC-K Rate: {formatCurrency(previewData.rateTable.smcKRate)}</h6>
                  <h6 className="mb-2 mt-3"><strong>SMC-S Rates</strong></h6>
                  <SmcSConfigurationsTable smcSRates={previewData.rateTable.smcSRates} />
                </div>
              )}

              {/* Filter Controls */}
              <div className="mb-4 p-3 border rounded bg-white">
                <h5 className="mb-3">Filter Configurations</h5>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-bold">Married</Form.Label>
                      <Form.Select
                        size="sm"
                        value={previewMarried}
                        onChange={(e) => setPreviewMarried(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="false">Single</option>
                        <option value="true">Married</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-bold">Children Under 18</Form.Label>
                      <Form.Select
                        size="sm"
                        value={previewChildrenUnder18}
                        onChange={(e) => setPreviewChildrenUnder18(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-bold">Children 18-24 (School)</Form.Label>
                      <Form.Select
                        size="sm"
                        value={previewChildrenInSchool}
                        onChange={(e) => setPreviewChildrenInSchool(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-bold">SMC-K Count</Form.Label>
                      <Form.Select
                        size="sm"
                        value={previewSmcK}
                        onChange={(e) => setPreviewSmcK(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Monthly Compensation Matrix */}
              <h5 className="mb-3">Monthly Compensation Amounts by Configuration</h5>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <Table size="sm" bordered striped hover className="mb-0">
                  <thead className="sticky-top bg-white">
                    <tr>
                      <th style={{ minWidth: '80px' }}>Married</th>
                      <th style={{ minWidth: '60px' }}>Kids&lt;18</th>
                      <th style={{ minWidth: '60px' }}>School</th>
                      <th style={{ minWidth: '60px' }}>SMC-K</th>
                      <th className="text-end">10%</th>
                      <th className="text-end">20%</th>
                      <th className="text-end">30%</th>
                      <th className="text-end">40%</th>
                      <th className="text-end">50%</th>
                      <th className="text-end">60%</th>
                      <th className="text-end">70%</th>
                      <th className="text-end">80%</th>
                      <th className="text-end">90%</th>
                      <th className="text-end">100%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.configurations
                      .filter((config) => {
                        if (previewMarried !== 'all' && String(config.isMarried) !== previewMarried) return false;
                        if (previewChildrenUnder18 !== 'all' && String(config.childrenUnder18) !== previewChildrenUnder18) return false;
                        if (previewChildrenInSchool !== 'all' && String(config.childrenInSchool) !== previewChildrenInSchool) return false;
                        if (previewSmcK !== 'all' && String(config.smcKCount) !== previewSmcK) return false;
                        return true;
                      })
                      .map((config, idx) => (
                        <tr key={idx}>
                          <td>
                            <Badge bg={config.isMarried ? 'primary' : 'secondary'}>
                              {config.isMarried ? 'Yes' : 'No'}
                            </Badge>
                          </td>
                          <td className="text-center">{config.childrenUnder18}</td>
                          <td className="text-center">{config.childrenInSchool}</td>
                          <td className="text-center">{config.smcKCount}</td>
                          {config.amounts.map((amount, i) => (
                            <td key={i} className="text-end font-monospace" style={{ fontSize: '0.85rem' }}>
                              {formatCurrency(amount)}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3 text-muted small">
                Showing {previewData.configurations.filter((config) => {
                  if (previewMarried !== 'all' && String(config.isMarried) !== previewMarried) return false;
                  if (previewChildrenUnder18 !== 'all' && String(config.childrenUnder18) !== previewChildrenUnder18) return false;
                  if (previewChildrenInSchool !== 'all' && String(config.childrenInSchool) !== previewChildrenInSchool) return false;
                  if (previewSmcK !== 'all' && String(config.smcKCount) !== previewSmcK) return false;
                  return true;
                }).length} of {previewData.configurations.length} configurations
              </div>
            </>
          ) : (
            <Alert variant="danger">Failed to load preview</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Draft Rate Schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editRateData && (
            <>
              {/* Effective Date */}
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Effective Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={editEffectiveDate}
                      onChange={(e) => setEditEffectiveDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* SMC-K Rate */}
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">SMC-K Rate</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={editRateData.smcKRate}
                      onChange={(e) => handleEditRateChange('smcKRate', null, e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr />

              {/* Base Rates - Single */}
              <h5 className="mb-3">Single (Veteran Alone)</h5>
              <Table size="sm" bordered responsive className="mb-4">
                <thead>
                  <tr>
                    <th>Rating</th>
                    {['10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'].map((r) => (
                      <th key={r} className="text-center" style={{ width: '9%' }}>
                        {r}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-bold">Base</td>
                    {editRateData.chart.single.base.map((val, i) => (
                      <td key={i} className="p-1">
                        <Form.Control
                          type="number"
                          step="0.01"
                          size="sm"
                          value={val}
                          onChange={(e) => handleEditRateChange('chart.single.base', i, e.target.value)}
                          style={{ fontSize: '0.8rem' }}
                        />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">+1 Child</td>
                    {editRateData.chart.single.dep1.map((val, i) => (
                      <td key={i} className="p-1">
                        <Form.Control
                          type="number"
                          step="0.01"
                          size="sm"
                          value={val}
                          onChange={(e) => handleEditRateChange('chart.single.dep1', i, e.target.value)}
                          style={{ fontSize: '0.8rem' }}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>

              {/* Base Rates - Married */}
              <h5 className="mb-3">Married (With Spouse)</h5>
              <Table size="sm" bordered responsive className="mb-4">
                <thead>
                  <tr>
                    <th>Rating</th>
                    {['10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'].map((r) => (
                      <th key={r} className="text-center" style={{ width: '9%' }}>
                        {r}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-bold">Base</td>
                    {editRateData.chart.married.base.map((val, i) => (
                      <td key={i} className="p-1">
                        <Form.Control
                          type="number"
                          step="0.01"
                          size="sm"
                          value={val}
                          onChange={(e) => handleEditRateChange('chart.married.base', i, e.target.value)}
                          style={{ fontSize: '0.8rem' }}
                        />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">+1 Child</td>
                    {editRateData.chart.married.dep1.map((val, i) => (
                      <td key={i} className="p-1">
                        <Form.Control
                          type="number"
                          step="0.01"
                          size="sm"
                          value={val}
                          onChange={(e) => handleEditRateChange('chart.married.dep1', i, e.target.value)}
                          style={{ fontSize: '0.8rem' }}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>

              {/* Additional Dependent Rates */}
              <h5 className="mb-3">Additional Child Rates</h5>
              <Table size="sm" bordered responsive className="mb-4">
                <thead>
                  <tr>
                    <th>Type</th>
                    {['10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'].map((r) => (
                      <th key={r} className="text-center" style={{ width: '9%' }}>
                        {r}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-bold">Each Child Under 18</td>
                    {editRateData.childRates.map((val, i) => (
                      <td key={i} className="p-1">
                        <Form.Control
                          type="number"
                          step="0.01"
                          size="sm"
                          value={val}
                          onChange={(e) => handleEditRateChange('childRates', i, e.target.value)}
                          style={{ fontSize: '0.8rem' }}
                        />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">School Child (18-24)</td>
                    {editRateData.schoolChildRates.map((val, i) => (
                      <td key={i} className="p-1">
                        <Form.Control
                          type="number"
                          step="0.01"
                          size="sm"
                          value={val}
                          onChange={(e) => handleEditRateChange('schoolChildRates', i, e.target.value)}
                          style={{ fontSize: '0.8rem' }}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </PrivateLayout>
  );
};

export default VaRatesManager;
